// ccp/src/lib/liveCopilotTriggers.js
//
// Live Copilot Phase 1 (backlog register #11) — the reactive knowledge-
// answer chip's trigger mechanism, as PURE functions (no React, no fetch),
// mirroring lib/coachingTriggers.js's already-proven shape exactly: same
// debounce constant (Storm, 2026-09-24 — reuse coaching's 30s floor, no
// reason to diverge for v1), same coachingView()-style adapter, same
// evaluateTriggers/newState/markFired API. scripts/verify-live-copilot-
// triggers.mjs proves this without a browser.
//
// KEYWORD/SENTIMENT, not semantic (vault 04-decisions.md #239, Storm,
// 2026-09-24). TRIGGERS below carries the FOUR categories Storm approved
// from the candidate list pulled from docs/km/customer-kb/ (price-cost,
// definitional, timing, data-privacy-security) — deliberately generic
// question-shape markers, not tuned to any one tenant's business.
//
// TWO CANDIDATE CATEGORIES DELIBERATELY HELD, NOT IN TRIGGERS
// (permission/possibility: "can I", "can you", "is there", "are there";
// capability: "do you", "does it"): Storm's concern was these fire on
// ordinary conversational phrases ("can you hold on", "do you know if")
// as readily as genuine questions. Checked against 32 real CUSTOMER lines
// from today's actual contacts (2026-09-24) — zero matches either way. The
// sample is too thin and too unrepresentative (almost entirely Storm's own
// internal test calls, not organic customer conversation — "I want a
// refund", fragmented ASR words) to support a real false-positive rate in
// either direction. Not included until real production volume exists to
// check against, or Storm rules without that data.
//
// data-privacy-security is intentionally broad for v1 (Storm, 2026-09-24)
// — flagged for tightening before it carries real weight, since it's the
// most likely category to feed compliance-tier chips once Phase 3 lands.
//
// D35 (2026-09-16, knktr-agent-coaching): the deployed v1 there evaluated
// `display` directly, whose `.transcript` is a permanent [] on a live call
// (connectData.js — still true today, checked before this file was
// written). liveCopilotView() below is the ONE place this module's
// transcript comes from; the caller (useLiveCopilot.js) must pass the real
// feed from useTranscript().lines through it, never raw `display`.
// scripts/verify-live-copilot-transcript-source.mjs guards this file and
// the hook against repeating that exact mistake, written before any trigger
// content — see that script for what it checks and why.

export const DEBOUNCE_MS = 30_000;

// The ONE place this module's transcript comes from. `feedLines` is the
// Phase 3 feed (useTranscript().lines — live poller, or the demo seed).
// display.transcript is deliberately overwritten, never read.
export function liveCopilotView(display, feedLines) {
  return { ...(display ?? {}), transcript: Array.isArray(feedLines) ? feedLines : [] };
}

function recentLines(display, n = 8) {
  const lines = Array.isArray(display?.transcript) ? display.transcript : [];
  return lines
    .filter(l => l && !l.typing && typeof l.text === "string")
    .slice(-n)
    .map(l => ({ speaker: l.speaker, text: l.text, sentiment: l.sentiment ?? null }));
}

// The most recent CUSTOMER line and its index in the full transcript — the
// index becomes part of the fire key so a trigger fires at most once per
// qualifying line, never repeatedly for the same utterance while other
// lines accumulate around it.
function lastCustomerLine(view) {
  return recentCustomerLines(view)[0] ?? null;
}

// The customer's last few lines, newest first (2026-10-06, backlog row 83).
// Looking at ONLY the last line missed real triggers: speech recognition emits
// fragments ("All", "Yeah", "OK") as their own customer lines, and two lines
// often arrive in one 3 s poll, so "I was sent someone else's personal
// information." was already followed by "All" when the console first saw it
// (contact a7c6366d). Up to RECENT_CUSTOMER_LINES lines are considered, and only
// those that began within RECENT_WINDOW_MS of the newest line in the transcript,
// so an old line cannot fire late. Lines without a timestamp (demo feed) count
// as recent. `key` is the line's own id when it has one: a late-arriving earlier
// line shifts array positions, an id does not.
const RECENT_CUSTOMER_LINES = 3;
const RECENT_WINDOW_MS = 20_000;
function recentCustomerLines(view) {
  const lines = Array.isArray(view?.transcript) ? view.transcript : [];
  let newest = null;
  for (const l of lines) if (l && Number.isFinite(l.beginMs) && (newest === null || l.beginMs > newest)) newest = l.beginMs;
  const out = [];
  for (let i = lines.length - 1; i >= 0 && out.length < RECENT_CUSTOMER_LINES; i--) {
    const l = lines[i];
    if (!l || l.speaker !== "Cust" || l.typing || typeof l.text !== "string" || !l.text.trim()) continue;
    if (newest !== null && Number.isFinite(l.beginMs) && newest - l.beginMs > RECENT_WINDOW_MS) break;
    out.push({ text: l.text, index: i, key: l.id ?? i });
  }
  return out;
}

function keywordTrigger({ id, label, pattern, tier = "customer" }) {
  return {
    id,
    kind: "knowledge-answer",
    label,
    // Which of the tenant's knowledge bases answers this. "agent" = the
    // agent-only KB (procedures: escalation, refund authority). The browser
    // only sends the word; knktr-copilot-retrieve resolves the KB id itself.
    tier,
    evaluate(view, state) {
      for (const line of recentCustomerLines(view)) {
        if (!pattern.test(line.text)) continue;
        const key = `${id}:${line.key}`;
        if (state.firedKeys.has(key)) continue;
        return { key, context: { question: line.text, recentLines: recentLines(view) } };
      }
      return null;
    },
  };
}

// Four categories Storm approved (2026-09-24), from the candidate list
// pulled from docs/km/customer-kb/ — see the module comment above for what
// each is grounded in and for the two categories deliberately held out.
export const TRIGGERS = [
  // 2026-10-02 (Storm "go triggers", contact a7d03cd7): a caller said they had
  // been sent someone else's personal information, demanded someone senior and
  // threatened the ombudsman, and the panel stayed empty: none of the four
  // question shapes below matches a complaint. These three are about what the
  // AGENT should do, so they search the agent-only KB (tier "agent"); the
  // customer KB answers them with marketing copy ("only 1 in 26 unhappy
  // customers complains"). Privacy and complaint sit first so the specific
  // match wins over a generic "what is" in the same line.
  keywordTrigger({
    id: "privacy-incident",
    label: "Possible privacy incident",
    tier: "agent",
    pattern: /\b(?:personal (?:information|details|data)|(?:someone|somebody) else(?:'s|s)? (?:\w+ ){0,3}(?:information|details|account|bill|invoice|data|statement)|wrong (?:person|customer|account)|(?:privacy|data) breach)\b/i,
  }),
  keywordTrigger({
    id: "complaint-escalation",
    label: "Complaint or escalation",
    tier: "agent",
    pattern: /\b(?:complain(?:t|ts|ing)?|ombudsman|escalat(?:e|ed|ing|ion)|(?:someone|somebody|anyone) (?:more )?senior|(?:your|a|the) (?:manager|supervisor|boss)|team leader|legal action|lawyer|solicitor)\b/i,
  }),
  keywordTrigger({
    id: "price-cost",
    label: "Price/cost question",
    // "How does pricing work?" (FAQ); the entire pricing doc's structure.
    pattern: /\b(how much|what(?:'s| is) the (?:price|cost|rate)s?|what does it cost|pricing|cost of)\b/i,
  }),
  keywordTrigger({
    id: "billing-refund",
    label: "Billing or refund",
    tier: "agent",
    pattern: /\b(?:refund(?:s|ed)?|(?:my|the|this|a|your) (?:bill|invoice)|billing|overcharged?|charged (?:me )?(?:twice|too much|incorrectly|wrong)|money back|credit note)\b/i,
  }),
  keywordTrigger({
    id: "definitional",
    label: "\"What is/does\" question",
    // "Does the AI replace my support team?", "What happens when the AI can't help?"
    pattern: /\bwhat (?:is|are|does|do|happens)\b/i,
  }),
  keywordTrigger({
    id: "timing",
    label: "Timing question",
    // "How long does it take to go live?", "What hours are human agents available?"
    pattern: /\b(?:how long|when (?:do|does|will|can|is)|what hours)\b/i,
  }),
  keywordTrigger({
    id: "data-privacy-security",
    label: "Data/privacy/security question",
    // Trust-security doc almost entirely; FAQ's data-residency and retention questions.
    // Deliberately broad for v1 — see the module comment above.
    pattern: /\b(?:data|privacy|secur(?:e|ely|ity)|stored|recording|transcript|retention)\b/i,
  }),
];

// Per-contact debounce state. Kept outside React so a remount mid-call
// cannot re-fire a trigger inside the floor.
export function newLiveCopilotState() {
  return { lastFiredAt: 0, firedKeys: new Set() };
}

// Decide what (if anything) to fire right now. Pure: mutates nothing.
// Returns { trigger, key, context } | { waitMs } | null. Same shape as
// coachingTriggers.js's evaluateTriggers, one trigger array, first match
// wins, no ranking.
export function evaluateTriggers(view, state, now = Date.now()) {
  for (const trigger of TRIGGERS) {
    const hit = trigger.evaluate(view, state);
    if (!hit) continue;
    const since = now - state.lastFiredAt;
    if (state.lastFiredAt && since < DEBOUNCE_MS) {
      return { waitMs: DEBOUNCE_MS - since };
    }
    return { trigger, key: hit.key, context: hit.context };
  }
  return null;
}

// Record a fire. Called only after the decision to call the retrieval
// Lambda is made, so a failed call still counts against the floor — the
// floor is a query-rate cap, not a delivery guarantee (same principle,
// same reasoning, as coachingTriggers.js's markFired).
export function markFired(state, key, now = Date.now()) {
  state.lastFiredAt = now;
  state.firedKeys.add(key);
}

export const _internal = { recentLines, lastCustomerLine, recentCustomerLines, keywordTrigger };

// Storm 2026-10-23 (chip-rail spec v0.1: "hard cap of three"): up to three chips
// show at once, newest first; older ones dim; a fourth pushes the oldest off.
// Pure, so scripts/verify-live-copilot-chip.mjs can prove it.
export const MAX_CHIPS = 3;
export function addChip(chips, chip, max = MAX_CHIPS) {
  const list = Array.isArray(chips) ? chips : [];
  const next = [chip, ...list];
  return { chips: next.slice(0, max), dropped: next.slice(max) };
}
export function removeChip(chips, id) {
  return (Array.isArray(chips) ? chips : []).filter(c => c && c.id !== id);
}
